import { useMemo } from 'react';
import { Route, Routes } from 'react-router';
import { Trans } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useAgreements } from '@/models/agreements';
import {
  AGREEMENTS_PER_PAGE,
  AgreementsTable,
} from '@/routes/SettingsBeta/components/AgreementsTable';

import { Agreement as AgreementPage } from './Agreement/Agreement';
import { Filters } from './Filters/Filters';
import { FiltersStateContext, useFiltersState } from './Filters/FiltersState';

export const AgreementsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Agreements />} />
      <Route path="/:id/*" element={<AgreementPage />} />
    </Routes>
  );
};

const Agreements = () => {
  const filters = useFiltersState();

  const queryParams = useMemo((): Endpoints['GET /agreements']['query'] => {
    const params: Endpoints['GET /agreements']['query'] = {
      limit: AGREEMENTS_PER_PAGE,
      offset: (filters.page.page - 1) * AGREEMENTS_PER_PAGE,
      search: filters.search.debouncedSearch || undefined,
    };

    if (filters.agreementTypes.agreementTypes.length > 0) {
      params.types = filters.agreementTypes.agreementTypes;
    }

    if (filters.articleBrands.articleBrands.length > 0) {
      params.articleBrandIds = filters.articleBrands.articleBrands.map(({ id }) => id);
    }

    if (filters.entityOrGroupSelections.entityOrGroupSelections.length > 0) {
      params.entityOrGroupIds = filters.entityOrGroupSelections.entityOrGroupSelections.map(
        ({ id }) => id
      );
    }

    if (filters.articleWorkshopSelections.articleWorkshopSelections.length > 0) {
      params.articleWorkshopIds = filters.articleWorkshopSelections.articleWorkshopSelections.map(
        ({ id }) => id
      );
    }

    const hasClientSelection = filters.hasClient.hasClient;

    if (hasClientSelection.length === 1) {
      params.hasClient = hasClientSelection[0];
    }

    return params;
  }, [
    filters.page.page,
    filters.search.debouncedSearch,
    filters.agreementTypes.agreementTypes,
    filters.articleBrands.articleBrands,
    filters.entityOrGroupSelections.entityOrGroupSelections,
    filters.articleWorkshopSelections.articleWorkshopSelections,
    filters.hasClient.hasClient,
  ]);

  const {
    data: { agreements, meta } = { agreements: [] },
    isLoading: isAgreementsLoading,
    isPlaceholderData: isAgreementsPlaceholder,
    isError: isAgreementsError,
  } = useAgreements(queryParams, { keepPreviousData: true });

  return (
    <main style={{ gap: '1rem' }}>
      <FiltersStateContext value={filters}>
        <Filters />
      </FiltersStateContext>
      <TableQueryWrapper
        isLoading={isAgreementsLoading || isAgreementsPlaceholder}
        isError={isAgreementsError}
      >
        {agreements.length === 0 ? (
          <EmptyState
            illustration={<IllustrationNoResults />}
            subtitle={
              <Stack>
                <Paragraph>
                  <Trans id="settings.organization.entities.agreements.no-results.1">
                    No results are matching your criteria.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans id="settings.organization.entities.agreements.no-results.2">
                    Please try with different keywords or filters.
                  </Trans>
                </Paragraph>
              </Stack>
            }
          />
        ) : (
          <AgreementsTable
            agreements={agreements}
            page={filters.page.page}
            onPageChange={filters.page.setPage}
            count={meta?.count}
          />
        )}
      </TableQueryWrapper>
    </main>
  );
};
